import { useEffect, useRef } from 'react'
import cn from 'classnames'
import { IArcVoiceLevels } from '../../helpers/arcVoice/realtimeVoiceClient'
import styles from './ArcVoice.module.scss'

export enum ArcVoiceOrbState {
  Connecting = 'connecting',
  Listening = 'listening',
  Hearing = 'hearing',
  Thinking = 'thinking',
  Speaking = 'speaking',
  Paused = 'paused',
}

interface IProps {
  state: ArcVoiceOrbState
  /** Read every frame; the orb swells with whoever is talking. */
  getLevels: () => IArcVoiceLevels
  size?: 'lg' | 'sm'
}

/** How much of each new frame's level shows, so the orb breathes rather than jitters. */
const SMOOTHING = 0.25

const levelFor = (state: ArcVoiceOrbState, { input, output }: IArcVoiceLevels): number => {
  if (state === ArcVoiceOrbState.Speaking) return output
  if (state === ArcVoiceOrbState.Hearing || state === ArcVoiceOrbState.Listening) return input
  return 0
}

/**
 * The call's face: a glowing orb that swells with the voice on the line, turns
 * while Arc works, and goes still when paused. The level is written straight to a
 * CSS variable each frame, so it never re-renders React.
 */
const ArcVoiceOrb = ({ state, getLevels, size = 'lg' }: IProps) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const node = ref.current
    if (!node) return undefined
    let level = 0
    let frame = 0
    const tick = () => {
      level += (levelFor(state, getLevels()) - level) * SMOOTHING
      node.style.setProperty('--level', level.toFixed(3))
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [state, getLevels])

  return (
    <div
      ref={ref}
      className={cn(styles.orb, size === 'sm' && styles.orbSmall)}
      data-state={state}
      aria-hidden
    >
      <span className={styles.orbHalo} />
      <span className={styles.orbCore} />
      <span className={styles.orbSheen} />
    </div>
  )
}

export default ArcVoiceOrb
